// src/NewTemplateModal.js
// Create a new agreement template — either a copy of an existing one or a blank
// skeleton. The result is a normal product in the library that you then shape in
// the builder (text sections) and the pricing-table editor (rows).
//
// Cloning re-keys every pricing row id server-side. That matters: copying ids
// verbatim is what created the cross-product collisions in the existing
// templates, where initialing a row in one table silently marks its twin in
// another and both get billed.
import { useState } from "react";

export default function NewTemplateModal({ products, onClose, onCreated }) {
  const [mode, setMode] = useState("clone");
  const [name, setName] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [description, setDescription] = useState("");
  const [withPricing, setWithPricing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const cloneable = (products || []).filter(p => p.id !== "custom");
  const canSave = name.trim() && (mode === "scratch" || sourceId);

  const create = async () => {
    if (!canSave) return;
    setSaving(true); setError(null);
    try {
      const res = await fetch("/api/products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(), mode,
          sourceId: mode === "clone" ? sourceId : undefined,
          description: description.trim() || undefined,
          includePricingTable: mode === "scratch" ? withPricing : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || "Could not create the template"); return; }
      onCreated?.(data.product, data.remappedRowIds || 0);
    } catch {
      setError("Could not reach the server. Nothing was created.");
    } finally { setSaving(false); }
  };

  const optionCard = (value, title, blurb) => (
    <button type="button" onClick={() => setMode(value)}
      style={{
        flex: 1, textAlign: "left", cursor: "pointer", borderRadius: 8, padding: "12px 14px",
        border: `1.5px solid ${mode === value ? "#1a3a6e" : "#e3e6ef"}`,
        background: mode === value ? "#f4f7ff" : "#fff",
      }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: "#1a3a6e", marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 11.5, color: "#666", lineHeight: 1.5 }}>{blurb}</div>
    </button>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
         onClick={() => !saving && onClose()}>
      <div style={{ background: "#fff", borderRadius: 12, width: 560, maxWidth: "95vw", boxShadow: "0 8px 40px rgba(0,0,0,0.25)" }}
           onClick={e => e.stopPropagation()}>

        <div style={{ padding: "20px 24px 14px", borderBottom: "1px solid #eef0f6" }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#1a3a6e" }}>New agreement template</div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            Creates a reusable product in your library. You can edit its text in the builder and its
            pricing rows with <strong>Edit table</strong>.
          </div>
        </div>

        <div style={{ padding: "16px 24px" }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            {optionCard("clone", "Copy an existing one", "Best when the new agreement is a variant — e.g. next year's version of an upgrade.")}
            {optionCard("scratch", "Start from scratch", "A blank agreement skeleton: title block, pricing, terms, signature.")}
          </div>

          <label style={{ fontSize: 11, fontWeight: 700, color: "#8a90a6", letterSpacing: ".03em" }}>TEMPLATE NAME</label>
          <input className="input" autoFocus value={name} onChange={e => setName(e.target.value)}
                 onKeyDown={e => { if (e.key === "Enter" && canSave) create(); }}
                 placeholder="e.g. CQMsolution 27 Upgrade"
                 style={{ width: "100%", boxSizing: "border-box", margin: "4px 0 14px" }} />

          {mode === "clone" && (
            <>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#8a90a6", letterSpacing: ".03em" }}>COPY FROM</label>
              <select className="input" value={sourceId} onChange={e => setSourceId(e.target.value)}
                      style={{ width: "100%", margin: "4px 0 14px" }}>
                <option value="">Choose a template…</option>
                {cloneable.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </>
          )}

          {mode === "scratch" && (
            <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#444", marginBottom: 14 }}>
              <input type="checkbox" checked={withPricing} onChange={e => setWithPricing(e.target.checked)} />
              Include an empty pricing table
            </label>
          )}

          <label style={{ fontSize: 11, fontWeight: 700, color: "#8a90a6", letterSpacing: ".03em" }}>DESCRIPTION (OPTIONAL)</label>
          <input className="input" value={description} onChange={e => setDescription(e.target.value)}
                 placeholder="Shown on the module card in the library"
                 style={{ width: "100%", boxSizing: "border-box", margin: "4px 0 0" }} />

          {error && (
            <div style={{ marginTop: 14, padding: "9px 12px", background: "#fdecea", border: "1px solid #f5c6cb", borderRadius: 6, fontSize: 12, color: "#a3342a" }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid #eef0f6", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} disabled={saving}
                  style={{ padding: "9px 18px", borderRadius: 7, border: "1px solid #ddd", background: "#fff", color: "#555", fontSize: 13, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={create} disabled={!canSave || saving}
                  style={{ padding: "9px 20px", borderRadius: 7, border: "none", background: "#1a3a6e", color: "#fff", fontSize: 13, fontWeight: 600,
                           cursor: !canSave || saving ? "not-allowed" : "pointer", opacity: !canSave || saving ? .5 : 1 }}>
            {saving ? "Creating…" : "Create template"}
          </button>
        </div>
      </div>
    </div>
  );
}
